import {
  asyncClipboardReadable,
  asyncClipboardWritePresent,
  blockFragment,
  CLIPBOARD_MIME_DOCZ,
  CLIPBOARD_MIME_HTML,
  CLIPBOARD_MIME_PLAIN,
  type ClipboardReadPermission,
  type ClipboardWriteResult,
  clipboardFlavours,
  clipboardWriteSupported,
  type DoczHandle,
  fragmentIntoBlock,
  fragmentToBlock,
  legacyFromStructuredBlock,
  parseClipboard,
  runNativeClipboardCommand,
  sliceFragment,
  type StructuredBlock,
  watchClipboardReadPermission,
  writeFlavoursTo,
  writeFlavoursToSystem,
} from "@norkron/web-docz";
import {
  canvasHasSelection,
  createFormatPainter,
  createShortcutDispatcher,
  type FormatPainter,
  type ShortcutHandler,
  type SystemPasteResult,
  type ToolbarEnv,
} from "@norkron/web-elaborate-editor";
import {
  type CanvasEditorHandle,
  canvasDoczBlocks,
  type ClipboardFlavoursLike,
  DoczEditBridge,
} from "@norkron/web-page-layout";
import { FormatBridge, overridesOf } from "./canvasFormatBridge";
import type { CanvasFormatLayer } from "./canvasFormatLayer";
import { CEREBR8_PAGE_BOX } from "./canvasPageMargins";

const UTF8 = new TextEncoder();

/**
 * The STRUCTURED block that owns each CANVAS block ordinal.
 *
 * `handle.toBlocks()` is NOT index-parallel with the canvas coordinate: a `list`
 * flattens to one canvas block PER ITEM and a page break / spacer / bookmark
 * flattens to NONE, so `toBlocks()[blockOrd]` names a different block as soon as
 * the document holds any of them. Repeat each structured block once per legacy
 * block it produces — the same flattening `web-page-layout`'s `buildCanvasView`
 * uses to define the coordinate in the first place.
 */
function structuredByCanvasOrd(handle: DoczHandle): StructuredBlock[] {
  const out: StructuredBlock[] = [];
  for (const sb of handle.toBlocks()) {
    for (let i = legacyFromStructuredBlock(sb).length; i > 0; i -= 1) out.push(sb);
  }
  return out;
}

/**
 * The PAGINATED surface's history / keyboard / clipboard / painter services.
 *
 * Before this file the canvas editor had none of them: `HiddenInputSink` mapped
 * caret navigation only, so ⌘Z, ⌘B and ⌘C literally did nothing on the paginated
 * surface, and the ribbon's History / Clipboard / Painter buttons had no host to
 * bind to. Everything here is COMPOSITION of primitives that already shipped
 * upstream (RULE ZERO — not one of these capabilities is re-implemented):
 *
 *  · HISTORY  → `EditorCore`'s bounded snapshot ring (web-page-layout, itself
 *               delegating to web-docz's shared `SnapshotHistory`). The undo unit
 *               is a docz IR snapshot taken through the WASM core — never a
 *               TypeScript shadow document (brief §10).
 *  · KEYBOARD → `createShortcutDispatcher` over the ONE command catalog, so a
 *               chord declared once works on BOTH surfaces. Caret gestures are
 *               resolved by the sink FIRST (mod+ArrowLeft / mod+Home are nav), and
 *               the dispatcher skips `group: 'clipboard'` so ⌘X/⌘C/⌘V always reach
 *               the native clipboard events — the only place `clipboardData` lives.
 *  · CLIPBOARD→ `clipboardFlavours` / `parseClipboard` (web-docz) fed through the
 *               sink's `readClipboard` / `onPasteFlavours` seams. The canvas has no
 *               contentEditable, so its copy source is the hidden input sink, NOT a
 *               DOM container — which is why the DOM `createClipboardController` is
 *               deliberately not reused here.
 *  · PAINTER  → `createFormatPainter` over the layer's real docz-backed `FormatApi`.
 *
 * HONEST LIMIT: the canvas mark model still lives in the format layer's override
 * map, and the legacy `DoczBlock` transport carries plain text + comments only. So
 * a PASTE lands as structure (paragraphs / headings / lists split into real blocks)
 * with its inline marks flattened, while a COPY is fully lossless (it reads the
 * styled fragment straight off the layer). Promoting the app to a structured docz
 * handle removes the asymmetry; it is not faked here.
 */

export interface CanvasEditorServicesDeps {
  /** The live canvas editor handle (its `.core` owns selection + history). */
  readonly getEditor: () => CanvasEditorHandle | null;
  /** The latest `DoczHandle` after the newest canvas edit. */
  readonly getHandle: () => DoczHandle;
  /** Publish a handle produced OUT of band (paste) back to the host. */
  readonly setHandle: (next: DoczHandle) => void;
  /** The format layer (styled fragments + the docz-backed `FormatApi`). */
  readonly getLayer: () => CanvasFormatLayer | null;
  /**
   * The table layer, for the enable environment the SHORTCUT dispatcher builds.
   *
   * Optional only because a caller may have no table model at all; when it is
   * absent the two gates below degrade to `false`, which is what this file
   * hardcoded before 2026-07-29 — and hardcoding them meant a keyboard-dispatched
   * table command was permanently disabled here while the very same command was
   * enabled in the ribbon. Two envs for one surface is two answers to one
   * question; this makes them the same answer.
   */
  readonly getTable?: () => {
    inTable(): boolean;
    hasCellRange(): boolean;
    tableHeaderOn(): boolean;
  } | null;
}

export interface CanvasEditorServices {
  undo(): boolean;
  redo(): boolean;
  canUndo(): boolean;
  canRedo(): boolean;
  historyBarrier(): void;
  /** The catalog dispatcher — also handed to the sink as `onShortcut`. */
  handleShortcut(ev: KeyboardEvent): boolean;
  /** Arm (capture) when disarmed, APPLY when armed. `false` = nothing happened. */
  formatPainter(sticky?: boolean): boolean;
  painterArmed(): boolean;
  /** Fire-and-forget button cut (see {@link cutToSystem} for the outcome). */
  cut(): void;
  /** Fire-and-forget button copy (see {@link copyToSystem} for the outcome). */
  copy(): void;
  /** Fire-and-forget button paste (see {@link pasteFromSystem} for the outcome). */
  paste(): void;
  /** Button copy with an HONEST outcome. */
  copyToSystem(): Promise<ClipboardWriteResult>;
  /** Button cut. Deletes the selection ONLY once the clipboard write is
   *  CONFIRMED — never before, never on an unverified `true`. */
  cutToSystem(): Promise<ClipboardWriteResult>;
  pasteFromSystem(): Promise<SystemPasteResult>;
  /** True when this browser can genuinely perform a BUTTON-initiated copy. */
  canCopy(): boolean;
  /** True when this browser can genuinely perform a BUTTON-initiated cut. */
  canCut(): boolean;
  canPaste(): boolean;
  /** The live `clipboard-read` permission, for a truthful button tooltip. */
  pastePermission(): ClipboardReadPermission;
  /** The sink seam: the three flavours for a native copy/cut (null = nothing selected). */
  readClipboard(): ClipboardFlavoursLike | null;
  /** The sink seam: a native paste (`true` claims the event). */
  onPasteFlavours(flavours: ClipboardFlavoursLike): boolean;
}

export function createCanvasEditorServices(
  deps: CanvasEditorServicesDeps,
): CanvasEditorServices {
  const { getEditor, getHandle, setHandle, getLayer } = deps;
  let painter: FormatPainter | null = null;
  let pasteSeq = 0;

  // The REAL `clipboard-read` permission, not merely the capability probe: the
  // Paste button used to stay enabled purely because `navigator.clipboard.read`
  // existed, then did nothing while the read hung on a prompt. `'unknown'`
  // (Firefox/Safari do not implement the permission name) is NOT a denial.
  let readPermission: ClipboardReadPermission = "unknown";
  watchClipboardReadPermission((state) => {
    readPermission = state;
  });

  /** The painter, built lazily over the layer's FormatApi (the layer is created
   *  after the services in the component's ref-init order). */
  const getPainter = (): FormatPainter | null => {
    if (painter) return painter;
    const api = getLayer()?.formatApi;
    if (!api) return null;
    painter = createFormatPainter(api);
    return painter;
  };

  // ── the enable environment this surface can HONESTLY declare ─────────────────
  const env = (): ToolbarEnv => {
    const layer = getLayer();
    const core = getEditor()?.core;
    const sel = core?.getState().selection;
    return {
      supports: layer?.formatApi.supports ?? {},
      // ONE definition of "there is a selection", shared with the surface's own
      // `ElaborateEditorHandle.hasSelection()` and with the DOM editor (RULE
      // ZERO). This used to be a hand-rolled anchor/focus comparison here — a
      // second copy of the upstream predicate, free to drift from the gate the
      // ribbon actually reads.
      hasSelection: canvasHasSelection(sel),
      inTable: deps.getTable?.()?.inTable() ?? false,
      hasCellRange: deps.getTable?.()?.hasCellRange() ?? false,
      tableHeaderOn: deps.getTable?.()?.tableHeaderOn() ?? false,
      // The SHEET the margins picker validates against. Declaring the plate's
      // reserved running-header/footer bands is what lets the picker refuse a
      // top margin that would make the exported PDF's running header overprint
      // the body text — the editor cannot know that on its own.
      pageBox: CEREBR8_PAGE_BOX,
      host: {
        undo: core?.canUndo() ?? false,
        redo: core?.canRedo() ?? false,
        // LIVE probes, not constants. `clipboard: true` was a lie in any browser
        // with no button-initiated write mechanism: it rendered Cut/Copy ENABLED
        // where Cut could only ever destroy the selection. An honest disabled
        // control is a feature; an enabled one that destroys data is the bug.
        clipboard:
          typeof document !== "undefined" && clipboardWriteSupported(document, "copy"),
        clipboardRead: asyncClipboardReadable() && readPermission !== "denied",
        painter: true,
      },
    } as ToolbarEnv;
  };

  const dispatch: ShortcutHandler = createShortcutDispatcher({
    getEnv: env,
    // A FACTORY — re-read live on every keystroke. `font.grow` steps from
    // `ctx.active.fontSize`, so a memoised snapshot would climb 12→14, 12→14, …
    getContext: () => {
      const layer = getLayer();
      const api = layer?.formatApi;
      if (!api) throw new Error("canvas format layer not mounted");
      return {
        api,
        active: { ...api.active(), painterArmed: getPainter()?.armed() === true },
        after: () => {},
        host: {
          insertBlock: () => {},
          insertPageBreak: () => {},
          undo: () => void undo(),
          redo: () => void redo(),
          canUndo: () => getEditor()?.core.canUndo() ?? false,
          canRedo: () => getEditor()?.core.canRedo() ?? false,
          formatPainter: () => void formatPainter(),
        },
      };
    },
  });

  // ── history ────────────────────────────────────────────────────────────────
  function undo(): boolean {
    const core = getEditor()?.core;
    if (!core?.canUndo()) return false;
    core.undo();
    return true;
  }

  function redo(): boolean {
    const core = getEditor()?.core;
    if (!core?.canRedo()) return false;
    core.redo();
    return true;
  }

  // ── clipboard ──────────────────────────────────────────────────────────────

  /**
   * The SELECTED blocks as mark-carrying structured blocks. A partial range is
   * sliced through the real `dz_transform_split_runs` verb so a cut that starts
   * mid-run keeps its marks. Empty = nothing to copy (a collapsed caret).
   *
   * ## ⛔ THE DATA LOSS THIS REPLACES — copying across a list
   *
   * The canvas ordinal is NOT one-per-structured-block: `structuredByCanvasOrd`
   * repeats the SAME block object once per legacy block it flattens into, so a
   * three-item list owns THREE consecutive ordinals. The old walk treated every
   * ordinal between the endpoints as its own block and ran
   * `fragmentIntoBlock(mid, layer.fragmentFor(ord))` on each — handing a LIST a
   * bare one-item fragment three times over. `fragmentIntoBlock` used to answer
   * that with `{ kind: 'paragraph', runs }`, so **selecting across a bullet list
   * and pressing Copy put three loose paragraphs on the clipboard** — `style`,
   * `bulletChar`, `startOverride`, `numId` and every item's `checked` / `level`
   * gone — and pressing Cut then deleted the real list, because `handleWrite`
   * deletes once the (shredded) write is confirmed. The list was unrecoverable.
   *
   * web-docz now REFUSES that downgrade (`StructuredBlock | null`). Left as it
   * was, the refusal turned into `out.push(null)` and `clipboardFlavours` threw
   * `TypeError: null is not an object` out of a fire-and-forget ribbon handler —
   * a crash instead of a shred. Neither is the answer.
   *
   * ## What it does now
   *
   * Walk DISTINCT structured blocks, not ordinals (identity comparison finds each
   * one's ordinal run, because the repeats are consecutive), and ask the question
   * the old code never asked: **is this block covered ENTIRELY?**
   *
   *  · **A run-bearing leaf** (paragraph / heading, and the containers that name
   *    exactly one editable region) is sliced as before. That path is lossless in
   *    the direction that matters — it reads the layer's STYLED fragment, so
   *    uncommitted marks are copied, and `fragmentIntoBlock` keeps the block's own
   *    kind and props. A full-range slice is simply the whole fragment.
   *  · **A container the selection covers WHOLLY** (a multi-item list, a table) is
   *    copied VERBATIM. There is no fragment write-back to do: the block IS the
   *    answer, structure included. This is the fix — the list stays a list.
   *  · **A container covered only in PART** yields its covered regions as leaf
   *    blocks. That is not a fallback for the refusal, it is the correct answer:
   *    half a bullet is text, no container kind can be rebuilt from one region's
   *    runs, and every editor behaves this way. It is spelled out explicitly, in
   *    one place, rather than being what `fragmentIntoBlock` happened to return.
   *
   * `blockFragment(base) === null` is the probe for "container", and deliberately
   * so: it is the SAME `concretePath(block, [])` predicate `fragmentIntoBlock`'s
   * own tiers branch on, so this cannot drift out of step with what the leaf will
   * accept.
   */
  function selectionBlocks(): StructuredBlock[] {
    const core = getEditor()?.core;
    const layer = getLayer();
    if (!core || !layer) return [];
    const { anchor, focus } = core.getState().selection;
    const handle = getHandle();
    const blocks = structuredByCanvasOrd(handle);
    const [lo, hi] =
      anchor.blockOrd < focus.blockOrd ||
      (anchor.blockOrd === focus.blockOrd && anchor.byteOffset <= focus.byteOffset)
        ? [anchor, focus]
        : [focus, anchor];
    if (lo.blockOrd === hi.blockOrd && lo.byteOffset === hi.byteOffset) return [];

    /** The byte range of canvas ordinal `ord` the selection covers. Only the two
     *  ENDPOINT ordinals can be partial; everything between them is whole. */
    const rangeAt = (ord: number): { start: number; end: number } => ({
      start: ord === lo.blockOrd ? lo.byteOffset : 0,
      end: ord === hi.blockOrd ? hi.byteOffset : blockByteLength(ord),
    });

    /** One ordinal's covered runs as a clipboard block, or `null` when the wasm
     *  slice itself failed. */
    const sliceAt = (ord: number): StructuredBlock | null => {
      const base = blocks[ord];
      if (!base) return null;
      const { start, end } = rangeAt(ord);
      const frag = layer.fragmentFor(ord) ?? blockFragment(base);
      if (!frag) return base;
      const res = sliceFragment(handle.module, frag, { start, end });
      if (!res.ok) return null;
      // A leaf takes the slice IN PLACE, keeping its kind and its props.
      const inPlace = fragmentIntoBlock(base, res.fragment);
      if (inPlace) return inPlace;
      // It REFUSED: `base` is a container and this is a PART of it (a whole one
      // never reaches here — see the walk below). The covered region's runs become
      // their own leaf. Explicit, because a container's structure genuinely does
      // not survive a partial copy and pretending otherwise is what shredded it.
      //
      // Keyed by the REGION's address (`L::li:1`), not the container's id: two
      // partially-copied items of one list would otherwise both claim to be `L`,
      // and a payload block should name where it came from.
      return fragmentToBlock(res.fragment, canvasBlocks()[ord]?.id ?? base.id);
    };

    const out: StructuredBlock[] = [];
    let ord = lo.blockOrd;
    while (ord <= hi.blockOrd) {
      const base = blocks[ord];
      if (!base) {
        ord += 1;
        continue;
      }
      // The ordinal run this ONE structured block owns. The repeats are
      // consecutive by construction (`structuredByCanvasOrd` pushes the same
      // object N times in a row), so identity finds both edges.
      let first = ord;
      while (first > 0 && blocks[first - 1] === base) first -= 1;
      let last = ord;
      while (blocks[last + 1] === base) last += 1;
      const to = Math.min(last, hi.blockOrd);

      const whollyCovered =
        first === ord &&
        last === to &&
        rangeAt(ord).start === 0 &&
        rangeAt(to).end === blockByteLength(to);

      if (whollyCovered && blockFragment(base) === null) {
        out.push(base);
      } else {
        for (let o = ord; o <= to; o += 1) {
          const part = sliceAt(o);
          // A FAILED slice is a wasm-level error, not an empty selection. Pushing
          // the survivors would hand the user a silently TRUNCATED copy — and on a
          // CUT, the delete is downstream of a "confirmed" write of exactly that
          // truncation. Fail the whole read instead: `readClipboard` returns null,
          // the button reports "empty", and nothing is deleted.
          if (!part) return [];
          out.push(part);
        }
      }
      ord = to + 1;
    }
    return out;
  }

  /** UTF-8 byte length of a block's plain text — the unit the canvas selection's
   *  `byteOffset` is expressed in (the geometry engine's block-relative offset). */
  function blockByteLength(ord: number): number {
    const text = canvasBlocks()[ord]?.text ?? "";
    return UTF8.encode(text).length;
  }

  /** The CANVAS document view — the block list a `blockOrd` indexes. NOT
   *  `handle.blocks`; see `canvasDoczBlocks` / `buildCanvasView` upstream. */
  function canvasBlocks(): readonly { readonly id?: string; readonly text: string }[] {
    const core = getEditor()?.core;
    return canvasDoczBlocks(core?.getState().bridge ?? new DoczEditBridge(getHandle())) ?? [];
  }

  /** Insert decoded blocks after the caret block as ONE undoable bridge swap. */
  function insertBlocks(blocks: readonly StructuredBlock[]): boolean {
    const editor = getEditor();
    if (!editor || blocks.length === 0) return false;
    const handle = getHandle();
    const ord = editor.core.getState().selection.focus.blockOrd;
    // The anchor is resolved in the CANVAS coordinate (the caret's), then only
    // accepted when the IR really holds a block with that id — an ordinal that
    // lands inside an exploded list/quote carries the island's id, and inserting
    // "after" it would drop the pasted blocks in the wrong place.
    let anchorId = canvasBlocks()[ord]?.id;
    if (anchorId == null || !handle.blocks.some((b) => b.id === anchorId)) return false;
    let next = handle;
    for (const structured of blocks) {
      for (const legacy of legacyFromStructuredBlock(structured)) {
        pasteSeq += 1;
        const id = `paste-${Date.now().toString(36)}-${pasteSeq}`;
        next = next.insertBlockAfter(anchorId, { ...legacy, id });
        anchorId = id;
      }
    }
    if (next === handle) return false;
    setHandle(next);
    // ONE undoable step: `applyBridge` banks a snapshot, swaps the bridge, relays
    // out and repaints — the same seam a formatting op uses. A `FormatBridge` (not
    // a bare `DoczEditBridge`) so the session's styled-run overrides stay INSIDE
    // the snapshotted state across a paste; minting a plain bridge here would drop
    // them out of the undo ring until the next format commit re-minted one.
    const carried = overridesOf(editor.core.getState().bridge);
    editor.applyBridge(new FormatBridge(next, carried ?? new Map()));
    return true;
  }

  function readClipboard(): ClipboardFlavoursLike | null {
    const blocks = selectionBlocks();
    if (blocks.length === 0) return null;
    const f = clipboardFlavours(getHandle().module, blocks);
    return { plain: f.plain, html: f.html, payload: f.payload };
  }

  function onPasteFlavours(flavours: ClipboardFlavoursLike): boolean {
    const blocks = parseClipboard(
      {
        payload: flavours.payload || null,
        html: flavours.html || null,
        plain: flavours.plain || null,
      },
      { module: getHandle().module },
    );
    return insertBlocks(blocks);
  }

  async function pasteFromSystem(): Promise<SystemPasteResult> {
    if (!asyncClipboardReadable()) return "unsupported";
    // A STANDING denial short-circuits: awaiting `read()` there would hang on a
    // prompt that can never be granted, which is what made the button feel dead.
    if (readPermission === "denied") return "denied";
    try {
      const items = await navigator.clipboard.read();
      let payload: string | null = null;
      let html: string | null = null;
      let plain: string | null = null;
      for (const item of items) {
        for (const type of item.types) {
          const text = await (await item.getType(type)).text();
          if (type.endsWith(CLIPBOARD_MIME_DOCZ)) payload ??= text;
          else if (type === CLIPBOARD_MIME_HTML) html ??= text;
          else if (type === CLIPBOARD_MIME_PLAIN) plain ??= text;
        }
      }
      // The RICH flavours, not `readText()` alone: reading only `text/plain` threw
      // away the lossless payload (and every mark with it) on a button paste.
      return onPasteFlavours({
        plain: plain ?? "",
        html: html ?? "",
        payload: payload ?? "",
        // HONEST: nothing decodable is `'empty'`, never the old `? "ok" : "ok"`.
      })
        ? "ok"
        : "empty";
    } catch {
      // NotAllowedError (the Chrome paste chip dismissed / the Firefox doorhanger)
      // or any other read failure — HONEST, never a silent no-op.
      return "denied";
    }
  }

  // ── the BUTTON write path ───────────────────────────────────────────────────
  //
  // THE SHIPPED BUG THIS REPLACES: the button used to dispatch a SYNTHETIC
  // `new ClipboardEvent(type)` at the hidden input sink. An untrusted
  // ClipboardEvent carries `clipboardData === null`, so the sink's writes no-opped
  // and its unconditional `true` told the caller the text was safely on the
  // clipboard — then the cut deleted it. Chromium HAS the constructor, so that was
  // the branch that ran: not a fallback, a path that always silently failed.
  //
  // `runNativeClipboardCommand` instead runs the REAL `document.execCommand`, so
  // the browser delivers a TRUSTED event (the only kind carrying a live
  // `clipboardData`) into ONE handler — the original design intent ("the BUTTON
  // and the keystroke run the exact same handler") achieved properly. The
  // mechanism itself lives upstream in `@norkron/web-docz` because BOTH editing
  // surfaces need it and a fix in only one is half a fix (RULE ZERO / HR13).

  /** Remove the current selection — the cut half, routed through the SAME
   *  `deleteBackward` intent the sink emits for ⌘X. */
  function deleteSelection(): void {
    getEditor()?.core.applyIntent({ type: "deleteBackward" });
  }

  /** THE handler. Receives the REAL, trusted event and writes the three flavours;
   *  returns `true` ONLY when the clipboard genuinely received them. */
  function handleWrite(ev: ClipboardEvent, andDelete: boolean): boolean {
    const f = readClipboard();
    if (!f) return false;
    if (!writeFlavoursTo(ev.clipboardData, f)) return false;
    ev.preventDefault();
    // THE data-loss guard: the delete is downstream of a CONFIRMED write. Always.
    if (andDelete) deleteSelection();
    return true;
  }

  function runNative(type: "copy" | "cut"): ClipboardWriteResult {
    if (typeof document === "undefined") return "unsupported";
    if (readClipboard() == null) return "empty";
    // Focus the sink first: `execCommand` targets the focused element, which on
    // this surface is the hidden input proxy that owns the OS caret.
    getEditor()?.focus();
    return runNativeClipboardCommand({
      doc: document,
      type,
      handle: (ev) => handleWrite(ev, type === "cut"),
    });
  }

  /** The ASYNC fallback for a browser with no `execCommand`. The caller deletes
   *  only once this has resolved `true`. Distinguishes "no mechanism at all"
   *  (`unsupported` — the host should disable the button) from "a mechanism
   *  refused" (`denied` — tell the user), never conflating the two. */
  async function writeSelectionAsync(): Promise<ClipboardWriteResult> {
    const f = readClipboard();
    if (!f) return "empty";
    if (!asyncClipboardWritePresent()) return "unsupported";
    return (await writeFlavoursToSystem(f)) ? "ok" : "denied";
  }

  async function copyToSystem(): Promise<ClipboardWriteResult> {
    const native = runNative("copy");
    if (native !== "unsupported") return native;
    return writeSelectionAsync();
  }

  async function cutToSystem(): Promise<ClipboardWriteResult> {
    const native = runNative("cut");
    if (native !== "unsupported") return native;
    // No trusted-event mechanism: write asynchronously and delete ONLY after the
    // clipboard has confirmed it holds the text.
    const out = await writeSelectionAsync();
    if (out !== "ok") return out;
    deleteSelection();
    return "ok";
  }

  // ── painter ────────────────────────────────────────────────────────────────
  function formatPainter(sticky = false): boolean {
    const p = getPainter();
    if (!p) return false;
    if (p.armed()) return p.apply();
    p.arm(sticky);
    return true;
  }

  return {
    undo,
    redo,
    canUndo: () => getEditor()?.core.canUndo() ?? false,
    canRedo: () => getEditor()?.core.canRedo() ?? false,
    historyBarrier: () => getEditor()?.historyBarrier(),
    handleShortcut: (ev) => dispatch(ev),
    formatPainter,
    painterArmed: () => getPainter()?.armed() === true,
    cut: () => {
      void cutToSystem();
    },
    copy: () => {
      void copyToSystem();
    },
    paste: () => {
      void pasteFromSystem();
    },
    copyToSystem,
    cutToSystem,
    pasteFromSystem,
    // HONEST capability: when no mechanism can write, the host must DISABLE the
    // button with a truthful tooltip. An honest disabled control is a feature; an
    // enabled one that destroys data is the bug.
    canCopy: () => typeof document !== "undefined" && clipboardWriteSupported(document, "copy"),
    canCut: () => typeof document !== "undefined" && clipboardWriteSupported(document, "cut"),
    canPaste: () => asyncClipboardReadable() && readPermission !== "denied",
    pastePermission: () => readPermission,
    readClipboard,
    onPasteFlavours,
  };
}
